import { useState, useCallback } from 'react';
import { Bill } from '../ClaimDetailsTypes';
import { BillVerdictStatus } from '../../../types';

interface UseBillActionsProps {
  onVerdictUpdate: (billId: number, verdict: BillVerdictStatus, amount: number, reason: string) => void;
  loading?: boolean;
}

export const useBillActions = ({ onVerdictUpdate, loading = false }: UseBillActionsProps) => {
  // Modal states
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [verdictEditModalOpen, setVerdictEditModalOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);

  // Handlers for approve/reject (new bills)
  const handleApproveClick = useCallback((bill: Bill) => {
    setSelectedBill(bill);
    setApprovalModalOpen(true);
  }, []);

  const handleRejectClick = useCallback((bill: Bill) => {
    setSelectedBill(bill);
    setRejectionModalOpen(true);
  }, []);

  // Handler for editing existing verdicts
  const handleVerdictClick = useCallback((bill: Bill) => {
    setSelectedBill(bill);
    setVerdictEditModalOpen(true);
  }, []);

  // Close handlers
  const handleCloseApprovalModal = useCallback(() => {
    setApprovalModalOpen(false);
    setSelectedBill(null);
  }, []);

  const handleCloseRejectionModal = useCallback(() => {
    setRejectionModalOpen(false);
    setSelectedBill(null);
  }, []);

  const handleCloseVerdictEditModal = useCallback(() => {
    setVerdictEditModalOpen(false);
    setSelectedBill(null);
  }, []);

  // Action handlers
  const handleApprove = useCallback((
    billId: number,
    amount: number,
    reason: string,
    verdict: BillVerdictStatus
  ) => {
    onVerdictUpdate(billId, verdict, amount, reason);
    handleCloseApprovalModal();
  }, [onVerdictUpdate, handleCloseApprovalModal]);

  const handleReject = useCallback((billId: number, reason: string) => {
    onVerdictUpdate(billId, 'rejected', 0, reason);
    handleCloseRejectionModal();
  }, [onVerdictUpdate, handleCloseRejectionModal]);

  const handleVerdictEdit = useCallback((
    billId: number,
    verdict: BillVerdictStatus,
    amount: number,
    reason: string
  ) => {
    onVerdictUpdate(billId, verdict, amount, reason);
    handleCloseVerdictEditModal();
  }, [onVerdictUpdate, handleCloseVerdictEditModal]);

  return {
    // State
    approvalModalOpen,
    rejectionModalOpen,
    verdictEditModalOpen,
    selectedBill,
    
    // Handlers
    handleApproveClick,
    handleRejectClick,
    handleVerdictClick,
    handleCloseApprovalModal,
    handleCloseRejectionModal,
    handleCloseVerdictEditModal,
    handleApprove,
    handleReject,
    handleVerdictEdit,
    
    // Props for modals
    modalProps: {
      loading,
      selectedBill,
    }
  };
};

export default useBillActions;
